import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/api/client";
import { CheckCircle2, Download, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BuyerOrderTracker, { useOrderPolling } from "@/components/OrderTracking";
import OrderStagePill from "@/components/OrderStagePill";
import BankTransferInstructions from "@/components/BankTransferInstructions";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchOrder = async () => {
    try { const r = await api.get(`/orders/${id}`); setOrder(r.data); } catch { /* noop */ }
  };
  useOrderPolling(fetchOrder, 15000, [id]);

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/orders/${id}/invoice.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CityShor-Invoice-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="order-success-page">
      {order?.payment_method === "bank_transfer" && order?.payment_status === "awaiting_confirmation" ? (
        <>
          <Clock className="h-14 w-14 mx-auto text-amber-500 mb-4" />
          <h1 className="font-display text-4xl mb-2" data-testid="order-success-title">Payment Pending Verification</h1>
          <p className="text-gray-600 mb-8">Please transfer the amount using the bank details below and share the payment reference with us. Your order will be confirmed once we verify your payment.</p>
        </>
      ) : (
        <>
          <CheckCircle2 className="h-14 w-14 mx-auto text-green-500 mb-4" />
          <h1 className="font-display text-4xl mb-2" data-testid="order-success-title">Order Placed!</h1>
          <p className="text-gray-600 mb-8">We have received your order and it is waiting for the vendor to confirm. You will receive updates on WhatsApp and email.</p>
        </>
      )}
      {order && <div className="mb-6"><OrderStagePill order={order} /></div>}
      {order && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-left">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Order Number</div>
          <div className="font-mono-tab text-sm">{order.id}</div>
          <div className="mt-4 space-y-1 text-sm">
            {typeof order.subtotal === "number" && (
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>₹{order.subtotal.toLocaleString("en-IN")}</span></div>
            )}
            {(() => {
              const taxes = +((order.product_gst || 0) + (order.packaging_gst || 0) + (order.shipping_gst || 0)).toFixed(2);
              return taxes > 0 ? (
                <div className="flex justify-between text-gray-600"><span>Taxes</span><span>₹{taxes.toLocaleString("en-IN")}</span></div>
              ) : null;
            })()}
            {typeof order.packaging === "number" && (
              <div className="flex justify-between text-gray-600"><span>Packaging</span><span>₹{order.packaging.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.shipping === "number" && (
              <div className="flex justify-between text-gray-600"><span>Shipping{order.shipping_zone ? ` (${order.shipping_zone})` : ""}</span><span>₹{order.shipping.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.platform_fee === "number" && order.platform_fee > 0 && (
              <div className="flex justify-between text-gray-600"><span>Platform fee</span><span>₹{order.platform_fee.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.cod_charges === "number" && order.cod_charges > 0 && (
              <div className="flex justify-between text-gray-600"><span>COD handling</span><span>₹{order.cod_charges.toLocaleString("en-IN")}</span></div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t flex justify-between"><span>Total</span><span className="font-display text-lg">₹{order.total.toLocaleString("en-IN")}</span></div>
          <div className="mt-2 flex justify-between text-sm"><span>Payment</span><span>{order.payment_method === "cod" ? "Cash on Delivery" : order.payment_method === "bank_transfer" ? "Bank Transfer" : "Online"}</span></div>
          <BankTransferInstructions order={order} testIdPrefix="order-success-bank" />
          <BuyerOrderTracker order={order} testIdPrefix="order-success-track" />
          <div className="mt-3 text-[11px] text-gray-500">Applicable GST has been added to the total as per each product's tax slab.</div>

          <div className="mt-5 pt-5 border-t">
            <Button
              data-testid="download-invoice-btn"
              onClick={downloadInvoice}
              disabled={downloading}
              variant="outline"
              className="w-full rounded-full h-11 border-gray-300"
            >
              <Download className="h-4 w-4 mr-2" />
              {downloading ? "Preparing invoice…" : "Download Tax Invoice (PDF)"}
            </Button>
            <p className="text-[11px] text-gray-500 mt-2 text-center">
              GSTR-1 compliant. Includes HSN, CGST or IGST breakdown.
            </p>
          </div>
        </div>
      )}
      <div className="mt-8 flex gap-3 justify-center flex-wrap">
        {order && (
          <Link to={`/orders/${order.id}`}><Button data-testid="view-order-detail-btn" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">View Order Details</Button></Link>
        )}
        <Link to="/my-orders"><Button data-testid="view-orders-btn" variant="outline" className="rounded-full">All Orders</Button></Link>
        <Link to="/"><Button data-testid="continue-shopping-btn" variant="outline" className="rounded-full">Continue Shopping</Button></Link>
      </div>
    </div>
  );
}
